/**
 * TryoutLeadDrawer — slide-over with full per-lead details + personalization
 * controls. The original is shown but never editable. The user can toggle
 * which version is rendered on the landing.
 */
import { useState, useEffect, useCallback } from 'react';
import { X, Copy, ExternalLink, RefreshCw, Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { resolvePersonalization, composeFallback } from '@/lib/tryout/personalizationResolver';
import type { PersonalizationUse } from '@/lib/tryout/personalizationResolver';

export interface DrawerToken {
  id: string;
  token: string;
  is_active: boolean;
  usage_count: number;
  max_uses: number;
  expires_at: string | null;
  crm_lead_name: string | null;
  crm_lead_company: string | null;
  lead_first_name: string | null;
  lead_email: string | null;
  location: string | null;
  bundesland: string | null;
  berufsgruppe: string | null;
  personalization_original: string | null;
  personalization_ai_generated: string | null;
  personalization_ai_reason: string | null;
  personalization_custom: string | null;
  personalization_status: string | null;
  personalization_use: PersonalizationUse | null;
}

interface Props {
  token: DrawerToken | null;
  listId: string;
  onClose: () => void;
  onUpdated: () => void;
}

export const TryoutLeadDrawer: React.FC<Props> = ({ token, listId, onClose, onUpdated }) => {
  const [draftUse, setDraftUse] = useState<PersonalizationUse>('original');
  const [draftCustom, setDraftCustom] = useState('');
  const [saving, setSaving] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  useEffect(() => {
    if (token) {
      setDraftUse(token.personalization_use ?? 'original');
      setDraftCustom(token.personalization_custom ?? '');
    }
  }, [token]);

  const handleSave = useCallback(async () => {
    if (!token) return;
    setSaving(true);
    const updates: Record<string, unknown> = {
      personalization_use: draftUse,
    };
    if (draftUse === 'custom') {
      updates.personalization_custom = draftCustom.trim() || null;
    }
    await supabase.from('tryout_tokens').update(updates).eq('id', token.id);
    setSaving(false);
    onUpdated();
  }, [token, draftUse, draftCustom, onUpdated]);

  const handleRefreshToken = useCallback(async () => {
    if (!token) return;
    if (!confirm('Token neu generieren? Alte URL wird ungültig.')) return;
    setSaving(true);
    const newToken = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    await supabase.from('tryout_tokens')
      .update({ token: newToken, usage_count: 0 }).eq('id', token.id);
    setSaving(false);
    onUpdated();
  }, [token, onUpdated]);

  const handleRecheckPersonalization = useCallback(async () => {
    if (!token) return;
    setRechecking(true);
    // Reset just this token to pending; the function picks up pending+failed.
    await supabase.from('tryout_tokens')
      .update({ personalization_status: 'pending' }).eq('id', token.id);

    const supaUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const { data: { session } } = await supabase.auth.getSession();
    await fetch(`${supaUrl}/functions/v1/tryout-personalization-check`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ list_id: listId }),
    });
    setRechecking(false);
    onUpdated();
  }, [token, listId, onUpdated]);

  if (!token) return null;

  const tryoutUrl = `${window.location.origin}/tryout/${token.token}`;
  const fallbackText = composeFallback(token.berufsgruppe, token.location);

  const previewText = resolvePersonalization({
    personalization_original: token.personalization_original,
    personalization_ai_generated: token.personalization_ai_generated,
    personalization_custom: draftUse === 'custom' ? draftCustom : token.personalization_custom,
    personalization_use: draftUse,
    berufsgruppe: token.berufsgruppe,
    location: token.location,
  });

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[640px] max-w-full bg-background border-l overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold">{token.crm_lead_name ?? '—'}</h2>
          <Button size="icon" variant="ghost" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>

        <div className="p-4 space-y-4">
          {/* Lead daten */}
          <section className="space-y-2">
            <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Lead</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Firma:</span> {token.crm_lead_company ?? '—'}</div>
              <div><span className="text-muted-foreground">E-Mail:</span> {token.lead_email ?? '—'}</div>
              <div><span className="text-muted-foreground">Stadt:</span> {token.location ?? '—'}</div>
              <div><span className="text-muted-foreground">Bundesland:</span> {token.bundesland ?? '—'}</div>
              <div><span className="text-muted-foreground">Berufsgruppe:</span> {token.berufsgruppe ?? '—'}</div>
            </div>
          </section>

          {/* Token */}
          <section className="space-y-2">
            <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">Tryout-Token</h3>
            <div className="flex items-center gap-2">
              <Input value={tryoutUrl} readOnly className="font-mono text-xs" />
              <Button size="icon" variant="outline" onClick={() => navigator.clipboard.writeText(tryoutUrl)}>
                <Copy className="h-3 w-3" />
              </Button>
              <Button size="icon" variant="outline" onClick={() => window.open(tryoutUrl, '_blank')}>
                <ExternalLink className="h-3 w-3" />
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Status: {token.is_active ? 'Aktiv' : 'Deaktiviert'} · {token.usage_count}/{token.max_uses} Uses
            </div>
            <Button size="sm" variant="outline" onClick={handleRefreshToken} disabled={saving}>
              <RefreshCw className="h-3 w-3 mr-2" /> Token neu generieren
            </Button>
          </section>

          {/* Personalization */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                Personalisierung
                <span className="ml-2 px-2 py-0.5 rounded-full bg-muted text-xs normal-case">
                  {token.personalization_status ?? 'pending'}
                </span>
              </h3>
              <Button size="sm" variant="outline" onClick={handleRecheckPersonalization}
                disabled={rechecking || saving}>
                {rechecking
                  ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  : <Sparkles className="h-3 w-3 mr-1.5" />}
                KI-Check erneut
              </Button>
            </div>

            <div className="rounded-md border p-3 space-y-1">
              <div className="text-xs font-medium uppercase text-muted-foreground">Original (Elia, immutable)</div>
              <div className="text-sm">{token.personalization_original ?? <em>—</em>}</div>
            </div>

            <div className="rounded-md border p-3 space-y-1">
              <div className="text-xs font-medium uppercase text-muted-foreground">KI-generiert</div>
              {token.personalization_ai_generated ? (
                <>
                  <div className="text-sm">{token.personalization_ai_generated}</div>
                  {token.personalization_ai_reason && (
                    <div className="text-xs text-muted-foreground">Grund: {token.personalization_ai_reason}</div>
                  )}
                </>
              ) : <div className="text-sm text-muted-foreground italic">— (Original passt oder Check noch nicht durchgelaufen)</div>}
            </div>

            <div className="rounded-md border p-3 space-y-1">
              <div className="text-xs font-medium uppercase text-muted-foreground">Fallback (auto)</div>
              <div className="text-sm">{fallbackText ?? <em>nicht verfügbar (zu wenig Lead-Daten)</em>}</div>
            </div>

            <div className="rounded-md border p-3 space-y-1">
              <Label className="text-xs font-medium uppercase text-muted-foreground">Custom-Override</Label>
              <Textarea value={draftCustom} onChange={(e) => setDraftCustom(e.target.value)}
                placeholder="Eigener Text — wird nur angezeigt wenn 'Custom' ausgewählt." />
            </div>

            <div className="grid grid-cols-4 gap-2 pt-2">
              {(['original', 'ai_generated', 'fallback', 'custom'] as const).map((use) => (
                <label key={use}
                  className={`flex items-center gap-1.5 text-xs cursor-pointer rounded-md border p-2 ${draftUse === use ? 'bg-primary/10 border-primary' : ''}`}>
                  <input type="radio" name="use" checked={draftUse === use} onChange={() => setDraftUse(use)} />
                  {use === 'original' ? 'Original' : use === 'ai_generated' ? 'KI' : use === 'fallback' ? 'Fallback' : 'Custom'}
                </label>
              ))}
            </div>

            <div className="rounded-md bg-muted/50 p-3">
              <div className="text-xs uppercase text-muted-foreground mb-1">Wird auf Landing angezeigt:</div>
              <div className="text-sm">{previewText ?? <em>— keine Sub-Headline</em>}</div>
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-background border-t -mx-4 px-4 py-3">
            <Button variant="ghost" onClick={onClose}>Schließen</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Speichern
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
